import { Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import React, { useRef, useState } from "react";
import ToolTipUsingPortal, {
  type TooltipPlacement,
} from "../toolTipUsingPortal/toolTipUsingPortal";
import "./tagWithOverflow.scss";

/**
 * Props for the standalone tag + overflow control.
 *
 * Renders up to `maxVisibleTags` chips; remaining labels collapse into a
 * `+N` count whose hover tooltip lists the overflow items.
 */
export interface TagWithOverflowProps {
  /**
   * Tag labels. Up to `maxVisibleTags` entries are rendered as chips;
   * remaining entries drive the `+N` overflow count when `overflowCount`
   * is omitted.
   */
  tags?: string[];
  /**
   * How many `tags` to render as chips before collapsing the rest into the
   * `+N` overflow. Defaults to `1` (chip + overflow).
   */
  maxVisibleTags?: number;
  /**
   * Primary tag text when not using `tags`. Used as the chip label.
   */
  text?: string;
  /**
   * Explicit overflow count shown as `+N`. When omitted, derived from the
   * number of hidden `tags`.
   */
  overflowCount?: number;
  /**
   * Optional labels listed in the `+N` hover tooltip when `tags` does not
   * include the overflow items (e.g. when using `text` + `overflowCount`).
   */
  overflowLabels?: string[];
  /** Chip background color. Defaults to Primary Gray 16 (`#F5F5F5`). */
  backgroundColor?: string;
  /** Chip text color. Defaults to Primary Gray 1 (`#0F0F0F`). */
  foregroundColor?: string;
  /** Extra class names applied to each tag chip (e.g. for font styling). */
  tagClassName?: string;
  /** Extra class names applied to the `+N` overflow count. */
  countClassName?: string;
  /** Extra class names applied to the root container. */
  className?: string;
  /** Where the `+N` tooltip is anchored relative to the count. Defaults to `"top"`. */
  placement?: TooltipPlacement;
}

/**
 * Resolves which labels to show inside the `+N` hover tooltip.
 *
 * @param tags - Full tag list
 * @param overflowLabels - Explicit overflow labels when provided
 * @param maxVisibleTags - Number of tags rendered as chips
 * @returns Labels for the tooltip body
 */
function getOverflowTooltipLabels(
  tags: string[],
  overflowLabels: string[] | undefined,
  maxVisibleTags: number,
): string[] {
  if (overflowLabels && overflowLabels.length > 0) {
    return overflowLabels;
  }
  return tags.slice(maxVisibleTags);
}

/**
 * Standalone tag + overflow control (extracted from the clinical-trials
 * table cell pattern, Figma node 27:9708): gray NCT-style chips beside a
 * `+N` count. Hovering `+N` opens a portaled tooltip listing the remaining
 * items. Not tied to any table; use wherever the pattern is needed.
 *
 * @param props - Tags, visible count, colors, and optional styling class names
 * @returns Tag chips with optional overflow count + tooltip
 */
export const TagWithOverflow = (props: TagWithOverflowProps) => {
  const {
    tags = [],
    maxVisibleTags = 1,
    text,
    overflowCount,
    overflowLabels,
    backgroundColor = "#F5F5F5",
    foregroundColor = "#0F0F0F",
    tagClassName,
    countClassName,
    className,
    placement = "top",
  } = props;

  const overflowRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const safeTags = tags.filter(Boolean);
  const clampedMaxVisible = Math.max(1, maxVisibleTags);
  const visibleTags = safeTags.slice(0, clampedMaxVisible);
  const primaryText = visibleTags[0] ?? text ?? "";
  const resolvedOverflowCount =
    overflowCount ?? Math.max(0, safeTags.length - visibleTags.length);
  const tooltipLabels = getOverflowTooltipLabels(
    safeTags,
    overflowLabels,
    clampedMaxVisible,
  );

  if (!primaryText) {
    return null;
  }

  return (
    <div
      className={`tag-with-overflow ${className ?? ""}`.trim()}
      onClick={(e) => e.stopPropagation()}
    >
      {visibleTags.map((label, index) => (
        <Tags
          key={index}
          label={label}
          backgroundColor={backgroundColor}
          textColor={foregroundColor}
          shouldTruncate
          className={tagClassName}
        />
      ))}
      {resolvedOverflowCount > 0 ? (
        <>
          <div
            ref={overflowRef}
            className={`tag-with-overflow__count ${countClassName ?? ""}`.trim()}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            +{resolvedOverflowCount}
          </div>
          <ToolTipUsingPortal
            placement={placement}
            showTooltip={showTooltip && tooltipLabels.length > 0}
            parentRef={overflowRef}
            wrapText
            maxWidth="240px"
            offsetPx={8}
          >
            <div className="tag-with-overflow__tooltip-tags">
              {tooltipLabels.map((label, index) => (
                <Tags
                  key={index}
                  label={label}
                  backgroundColor={backgroundColor}
                  textColor={foregroundColor}
                />
              ))}
            </div>
          </ToolTipUsingPortal>
        </>
      ) : null}
    </div>
  );
};

export default TagWithOverflow;
